import React from 'react'

/**
 * Parses text to make URLs clickable and preserve line breaks
 * @param text - The text to parse
 * @returns React elements with clickable links and preserved formatting
 */
export function parseTextWithLinks(text: string): React.ReactNode {
  if (!text) return null

  // Regex to match URLs (including those without protocol)
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}(\/[^\s]*)?/gi

  // Split text by line breaks first to preserve them
  const lines = text.split('\n')

  return lines.map((line, lineIndex) => {
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    // Reset regex lastIndex for each line
    urlRegex.lastIndex = 0

    while ((match = urlRegex.exec(line)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index))
      }

      // Get the matched URL
      let url = match[0]
      let href = url

      // Add protocol if missing
      if (!url.match(/^https?:\/\//)) {
        // Check if it looks like a domain (has a dot and TLD)
        if (url.includes('.') && !url.startsWith('www.')) {
          // Only treat as URL if it has a valid TLD structure
          const tldMatch = url.match(/\.[a-zA-Z]{2,}(\/|$)/)
          if (tldMatch) {
            href = `https://${url}`
          } else {
            // Not a valid URL, treat as regular text
            parts.push(url)
            lastIndex = match.index + url.length
            continue
          }
        } else if (url.startsWith('www.')) {
          href = `https://${url}`
        }
      }

      // Create the link element
      parts.push(
        <a
          key={`${lineIndex}-${match.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline transition-colors"
          onClick={(e) => e.stopPropagation()} // Prevent modal from closing
        >
          {url}
        </a>
      )

      lastIndex = match.index + url.length
    }

    // Add remaining text after the last URL
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex))
    }

    // If no URLs were found, just return the line
    if (parts.length === 0) {
      parts.push(line)
    }

    // Add line break between lines (except for the last line)
    if (lineIndex < lines.length - 1) {
      return (
        <React.Fragment key={lineIndex}>
          {parts}
          <br />
        </React.Fragment>
      )
    }

    return <React.Fragment key={lineIndex}>{parts}</React.Fragment>
  })
}

/**
 * Extracts all URLs from a text string
 * @param text - The text to extract URLs from
 * @returns Array of URL strings
 */
export function extractURLs(text: string): string[] {
  if (!text) return []

  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}(\/[^\s]*)?/gi
  const matches = text.match(urlRegex) || []

  return matches.map(url => {
    if (!url.match(/^https?:\/\//)) {
      if (url.startsWith('www.') || (url.includes('.') && url.match(/\.[a-zA-Z]{2,}(\/|$)/))) {
        return `https://${url}`
      }
    }
    return url
  })
}